import React, { useState } from "react";
import {
  Box, Typography, Button, Paper, IconButton, Chip, Skeleton, CircularProgress, Menu, MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
// Iconsax, matching Inbox's own edit-icon convention (ChatInfoRail.jsx uses
// this exact icon+prop pattern in several places) — see icon-library-iconsax
// memory.
import { Edit2 as EditIconIx, Coin as CoinIconIx } from "iconsax-react";
// Same robot-face icon already used for the AI Agents nav-rail item
// (Rahim, 2026-09-01: "+ is not good" for the Create AI Agent button) —
// more on-brand/thematic than a generic add-circle glyph.
import AiFaceIcon from "../../../../Navbar/AiFaceIcon";
// eslint-disable-next-line no-unused-vars
import AgentsGetStartedPanel from "./AgentsGetStartedPanel";
// Same bold iOS-style toggle already used everywhere else on this page's
// Config/Actions steps (AgentConfigView.jsx, OfficeHoursSection.jsx,
// ResponseFormatSection.jsx) — this card's Active/Inactive switch was still
// the plain stock MUI Switch, the one spot on the page that never got
// swapped over (Rahim, 2026-09-04: "we changed it remember for the card").
import PillSwitch from "../components/PillSwitch";
// Shared dropdown-menu formula — the actual established "..." menu look
// across the app (Inbox's MessageCard.jsx row menu, the reference for this
// card's own menu, Rahim 2026-09-01). Reused directly per that file's own
// "import and spread instead of writing a new inline copy" instruction.
import { DROPDOWN_PAPER_SX, DROPDOWN_ITEM_SX } from "../../../../../utils/dropdownMenuStyles";

// MUI's default Skeleton fill is alpha(text.primary, 0.11) — against this
// card's plain white background.paper (light mode), that reads as barely-
// there near-white, not a visible grey (Rahim, 2026-09-04, screenshot:
// "change the colour of skeleton...to grey not white"). Explicit grey,
// still theme-aware for dark mode (where the default already reads fine).
const SKELETON_SX = {
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.13)" : "#e0e0e0"),
};

// Plain vertical 3-dot kebab — neither of Iconsax's "More"/"More2" icons is
// actually this (More = horizontal line of 3 dots, More2 = a triangular
// 3-dot cluster, checked directly in node_modules), so this is a small
// hand-built one instead (Rahim, 2026-09-01: "simple 3 dot one in one line
// vertically").
const KebabDotsIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="5" r="1.75" fill={color} />
    <circle cx="12" cy="12" r="1.75" fill={color} />
    <circle cx="12" cy="19" r="1.75" fill={color} />
  </svg>
);

// Plain MUI Button variant="outlined" defaults to color="primary" — which in
// this theme IS the brand green (materialTheme.js primary.main). Rahim
// (2026-09-01): "never ever do a green button" — every outlined button on
// this page uses this explicit neutral style instead (same one already
// established for Learn More/the tab-button row in AiSettings.jsx).
const neutralButtonSx = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 600,
};

// Shared bordered "stat chip" shell (Rahim, 2026-09-02: "its very too
// simple can you do something amazing with it and make it better") —
// replaces the old bare label+bar/label+number inline text with a real
// bordered card matching the app's other rounded-border chips (same border
// treatment as AgentCard/CreateAgentTile above), an icon, and a
// label-over-value stack instead of flat inline text.
// `loading` (Rahim, 2026-09-04 audit: "header stats... no skeleton") —
// used by both UsageMeter and CreditsBalance below, which previously fed
// `agentsLoading ? 0 : agents.length` / the initial `{remaining:0}` state
// straight into `value`, flashing "0 / 10" and "0" credits instead of
// showing nothing until the real numbers arrive.
function StatChip({ iconSlot, label, value, loading }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        pl: 1,
        pr: 1.75,
        py: 0.875,
      }}
    >
      {loading ? <Skeleton variant="rounded" width={32} height={32} sx={{ ...SKELETON_SX, borderRadius: "9px", flexShrink: 0 }} /> : iconSlot}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.125, minWidth: 0 }}>
        {loading ? (
          <>
            <Skeleton width={50} height={14} sx={SKELETON_SX} />
            <Skeleton width={34} height={16} sx={SKELETON_SX} />
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary", whiteSpace: "nowrap", lineHeight: 1.2 }}>
              {label}
            </Typography>
            {/* Dialed back from 1rem (Rahim, 2026-09-02: "these are a little
                too big and doesnt match our font sizing") — verified
                against the actual neighbor in this same row, the Learn
                More/Create New Agent buttons at 0.8125rem (the theme's
                real MuiButton size), not a far-away panel-header
                convention from elsewhere on the page. Matching that
                exactly reads right next to its own row. */}
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, whiteSpace: "nowrap", lineHeight: 1.2, color: "text.primary" }}>
              {value}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

// Exported (Rahim, 2026-09-01) so AiSettings.jsx can render these inline
// with the Create New Agent/Knowledge Sources button row instead of on
// their own line below — see [[ai-settings-page-rebuild-plan]].
export function UsageMeter({ label, used, limit, color = "primary", loading }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const nearLimit = pct >= 80;
  // AI credits are fractional (1 credit = 1 PKR, billed to 6 decimal places — see
  // aiBillingService.js) — the meter itself is a whole-number progress display, so floor
  // for display only; the precise balance still lives in the backend/DB untouched.
  const displayUsed = Math.floor(used);
  const displayLimit = Math.floor(limit);

  return (
    <StatChip
      loading={loading}
      label={label}
      value={
        <>
          {displayUsed}
          <Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.disabled" }}>
            {" "}/ {displayLimit}
          </Typography>
        </>
      }
      // Progress RING instead of a flat bar (Rahim: "something amazing") —
      // the AI-agent icon sits centered inside its own usage ring, so the
      // one glyph doubles as both the stat's icon and its progress visual.
      iconSlot={
        <Box sx={{ position: "relative", width: 32, height: 32, flexShrink: 0 }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={32}
            thickness={4}
            sx={{ position: "absolute", color: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)") }}
          />
          <CircularProgress
            variant="determinate"
            value={pct}
            size={32}
            thickness={4}
            color={nearLimit ? "warning" : color}
            sx={{ position: "absolute" }}
          />
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ width: 16, height: 16, color: "text.secondary" }}>
              <AiFaceIcon size="100%" color="currentColor" variant="Linear" />
            </Box>
          </Box>
        </Box>
      }
    />
  );
}

export function CreditsBalance({ balance, loading }) {
  // AI credits are fractional (1 credit = 1 PKR, billed to 6 decimal places — see
  // aiBillingService.js) — floor for display only; the precise balance still lives
  // in the backend/DB untouched. There's no fixed "limit" to show progress against
  // here (the wallet grows on top-up), so this just surfaces the current balance.
  const displayBalance = Math.floor(Math.max(0, balance));
  return (
    <StatChip
      loading={loading}
      label="Credits Balance"
      value={displayBalance}
      iconSlot={
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "9px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(124,58,237,0.22)" : "rgba(124,58,237,0.1)"),
            color: "#7c3aed",
          }}
        >
          <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CoinIconIx size="100%" color="currentColor" variant="Bold" />
          </Box>
        </Box>
      }
    />
  );
}

// Matches the stat row's meta-text convention (0.8125rem/500 — Inbox's own
// scale, see UsageMeter/CreditsBalance above) instead of variant="body2"
// (renders at a flat 0.625rem, see [[ui-gotchas]]) — was near-illegibly
// small. Same data (label + value), just readable sizing.
// Stacked (label above value) instead of side-by-side (Rahim, 2026-09-01:
// "add better spacing and align the items properly") — the fixed minWidth:120
// label didn't leave enough room next to its value on these narrower/squarer
// cards, wrapping awkwardly. Stacking avoids that at any card width.
// Label dropped a step below the 0.75rem badge token (was the same size as
// the Default/Active chips, reading as the same visual weight instead of a
// quieter caption) — value stays at 0.8125rem, the app's documented "meta"
// token ([[font-sizing-scale]]), so there's real separation in the
// hierarchy: label (quietest) → value (meta) → name (0.9375rem row-title).
function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}>{value}</Typography>
    </Box>
  );
}

// Shape/font/layout pass (Rahim, 2026-09-01) — same fields, same actions
// (Knowledge source/Created at/Used in workflows, Edit, delete), just
// restyled: rounder card, a genuinely visible border instead of the near-
// invisible default divider-color outline, and typography matched to
// Inbox's own scale (0.9375rem/600 title — MessageCard.jsx's row-title
// convention; 0.8125rem/500 meta rows). See [[ai-settings-page-rebuild-plan]].
function AgentCard({ agent, workflowCounts, onEdit, onDelete, onToggleActive }) {
  const knowledgeCount = (agent.knowledgeSourceIds || []).length;
  const workflowCount = workflowCounts?.[agent._id] ?? 0;
  const createdAt = agent.createdAt
    ? new Date(agent.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";

  // 3-dot settings menu (Rahim, 2026-09-01) — replaces the bare delete icon
  // that used to sit here; styled with the same shared dropdown formula as
  // Inbox's own row-menu (MessageCard.jsx). Edit moved in here too since a
  // settings menu with only one destructive item read oddly on its own —
  // the bottom-row Edit button is removed to avoid two ways to do the same
  // thing.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const closeMenu = () => setMenuAnchor(null);

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: "16px",
        border: "1.5px solid",
        borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.15)"),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // Squarer proportions (Rahim, 2026-09-01) — was a wide rectangle;
        // paired with narrower Grid columns below (md=4→lg=3) so width
        // shrinks enough to actually read square, not just a taller rectangle.
        // height:"100%" (not just minHeight) so this actually fills its Grid
        // item's stretched height — Grid rows already equalize item height
        // via flex, but a Paper with only minHeight doesn't fill that, so it
        // and CreateAgentTile's Paper were drifting to different heights as
        // content grew (Rahim, 2026-09-01: "make both card same size").
        // Width/height shrunk together, same ratio (~0.694), so the shape
        // is unchanged (Rahim, 2026-09-02: "reduce the height and width
        // together so it doesnt change the shape") — was 433/300, then
        // 412/286. Shrunk again, 286->240 (Rahim, 2026-09-03: "make these
        // card small as well ... for the laptop view meaning basic
        // responsiveness") — paired with the grid's own column width drop
        // below (412->340) so more cards fit per row on a real laptop
        // screen instead of leaving dead space after 2.
        minHeight: 250,
        height: "100%",
        // Matches the Test Agent panel's card fill (Rahim, 2026-09-02:
        // "give the same background colour for the outside ... that we
        // have given to the test agent card") — was transparent, blending
        // straight into the page's own background.default. Then refined
        // per-theme (Rahim: "for liht mood dont make the ... background
        // white keep it transparetn for white theme only dark mood gets
        // coloured") — white read too stark in light mode; dark mode keeps
        // the grey fill since it doesn't have that problem.
        bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "transparent"),
        // Simple border-darken hover, no shadow — same convention as the
        // Reports table hover (Rahim, 2026-09-01: "remove that hover
        // effect" → "use simple eye pleasing one").
        transition: "border-color 0.18s, filter 0.18s, opacity 0.18s",
        "&:hover": {
          borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.26)" : "rgba(0,0,0,0.25)"),
        },
        // Whole card greys out when inactive (Rahim, 2026-09-01: "so its
        // obvious ... user can see its inactive") — the Switch itself stays
        // fully interactive, opacity/filter don't block clicks.
        ...(agent.isActive === false && { filter: "grayscale(0.7)", opacity: 0.6 }),
      }}
    >
      {/* 3-dot settings trigger, absolute top-right — Delete only (Rahim,
          2026-09-01: "who told you to add the edit icon there i only
          wanted the delete one"). Edit stays as its own bottom-row button,
          untouched. */}
      <IconButton
        size="small"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        sx={{ position: "absolute", top: 20, right: 12, zIndex: 1, color: (theme) => theme.palette.text.secondary }}
      >
        <KebabDotsIcon size={18} />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { ...DROPDOWN_PAPER_SX, minWidth: 140 } } }}
      >
        <MenuItem
          dense
          sx={{ ...DROPDOWN_ITEM_SX, mx: 0.5, color: (theme) => theme.palette.error.main }}
          onClick={() => { closeMenu(); onDelete(agent); }}
        >
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* p:3/gap:2.25 -> 2.5/1.75 (Rahim, 2026-09-03: "make these card
          small as well") — matches the card's own shrunk minHeight above. */}
      <Box sx={{ p: 2.75, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Read-only pill at top-left (back to how it was originally) +
            the real working Switch stays at bottom-right next to Edit
            (Rahim, 2026-09-01) — pill shows state, switch is the control. */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {agent.isDefault && (
            <Chip label="Default" size="small" color="primary" sx={{ height: 24, fontSize: "0.8125rem", fontWeight: 600 }} />
          )}
          <Chip
            label={agent.isActive !== false ? "Active" : "Inactive"}
            size="small"
            color={agent.isActive !== false ? "success" : "default"}
            variant="outlined"
            sx={{ height: 24, fontSize: "0.8125rem", fontWeight: 600 }}
          />
        </Box>

        {/* Hardcoded emoji square, no functionality behind it yet (Rahim,
            2026-09-01) — same fixed-size-box-around-a-plain-emoji-character
            convention as the Lifecycle stage icon (LifecycleRow.jsx /
            InboxSettings.jsx's Lifecycle filter list), just with a visible
            tinted square background so it actually reads as a "box". */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: "9px", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
              fontSize: "1.25rem", lineHeight: 1,
            }}
          >
            {agent.icon || "🤖"}
          </Box>
          <Typography
            sx={{
              fontSize: "1rem", fontWeight: 600, lineHeight: 1.3, flex: 1, minWidth: 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              color: "text.primary",
            }}
          >
            {agent.name}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <InfoRow
            label="Knowledge source"
            value={knowledgeCount === 0 ? "No sources" : `${knowledgeCount} source${knowledgeCount !== 1 ? "s" : ""}`}
          />
          <InfoRow label="Created at" value={createdAt} />
          <InfoRow
            label="Used in workflows"
            value={`${workflowCount} workflow${workflowCount !== 1 ? "s" : ""}`}
          />
        </Box>
      </Box>

      {/* Edit button restored here, back to how it was — the 3-dot menu is
          Delete-only. Left/right padding matches the content Box above
          (pl/pr: 3, same as its p: 3) — was px: 2.5, 4px short on the left,
          so Edit visibly didn't line up with the chip/robot-icon/InfoRow
          column above it (Rahim, 2026-09-02: "allign the items ... they
          dont match from the left side"). */}
      <Box sx={{ pl: 2.5, pr: 2.5, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EditIconIx size="100%" color="currentColor" variant="Linear" />
            </Box>
          }
          onClick={() => onEdit(agent)}
          sx={{ ...neutralButtonSx, fontSize: "0.875rem" }}
        >
          Edit
        </Button>
        <PillSwitch
          checked={agent.isActive !== false}
          onChange={(e) => onToggleActive(agent._id, e.target.checked)}
        />
      </Box>
    </Paper>
  );
}

// Rebuilt (Rahim, 2026-09-04: "make sure its using skeleton for our new
// design currently its using past one") — the old skeleton was a leftover
// generic avatar+2-lines+3-bars shape from before the 2026-09-01 card
// restyle. This one mirrors AgentCard's actual current structure/sizing
// section-for-section: same p:2.75/minHeight:250/height:100% shell, a
// chip-row placeholder up top, the 36px icon-square + title row, three
// stacked label/value pairs matching InfoRow's own two-line shape, and a
// footer placeholder matching the real Edit button + PillSwitch (40x24,
// same radius PillSwitch's track uses) instead of the old single bare bar.
function SkeletonCard() {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1.5px solid",
        borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.15)"),
        // Was plain white (Paper's own default background.paper) — never
        // matched the real AgentCard's own bg formula below it, which is
        // "transparent" in light mode so the page's grey shows through, and
        // background.paper only in dark mode (Rahim, 2026-09-04: "the
        // parent colour is white which i dont want, nobody ever uses white
        // background"). Same rule the real card already follows, just
        // never applied to its own skeleton.
        bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "transparent"),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 250,
        height: "100%",
      }}
    >
      <Box sx={{ p: 2.75, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        <Skeleton variant="rounded" width={64} height={24} sx={{ ...SKELETON_SX, borderRadius: "16px" }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Skeleton variant="rounded" width={36} height={36} sx={{ ...SKELETON_SX, borderRadius: "9px", flexShrink: 0 }} />
          <Skeleton width="60%" height={24} sx={SKELETON_SX} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Skeleton width="40%" height={14} sx={SKELETON_SX} />
              <Skeleton width="55%" height={18} sx={SKELETON_SX} />
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ pl: 2.5, pr: 2.5, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Skeleton variant="rounded" width={72} height={32} sx={{ ...SKELETON_SX, borderRadius: "8px" }} />
        <Skeleton variant="rounded" width={40} height={24} sx={{ ...SKELETON_SX, borderRadius: "12px" }} />
      </Box>
    </Paper>
  );
}

// "Create new AI Agent" tile (Rahim, 2026-09-01) — matches the agent cards'
// own shape/border, styled after the reference mock. Wired to the same
// onCreateNew handler already used by the top-of-page "Create New Agent"
// button — a second entry point into existing functionality, not new
// functionality. See [[ai-settings-page-rebuild-plan]].
function CreateAgentTile({ onCreateNew }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1.5px dashed",
        borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.15)"),
        // Matches AgentCard's own minHeight exactly, same history/reasoning
        // — see that component's comment.
        minHeight: 250,
        height: "100%",
        bgcolor: "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
        px: 2.5,
        transition: "border-color 0.18s",
        "&:hover": {
          borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.26)" : "rgba(0,0,0,0.25)"),
        },
      }}
    >
      <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>Create new AI Agent</Typography>
      {/* Matches the agent card's InfoRow label tier exactly (0.75rem/500,
          text.secondary), not its value tier — this plays the same "quiet
          caption" role. Rahim (2026-09-01): "match the ai card". */}
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary", mb: 1 }}>
        Add a new AI Agent to your team.
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={
          <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AiFaceIcon size="100%" color="currentColor" variant="Linear" />
          </Box>
        }
        onClick={onCreateNew}
        sx={{ ...neutralButtonSx, fontSize: "0.8125rem" }}
      >
        Create AI Agent
      </Button>
    </Paper>
  );
}

export default function AgentsTab({
  agents, loading, workspaceId, onCreateNew, onEdit, onDelete, onToggleActive, workflowCounts = {},
}) {
  // "AI Agents" heading/subtitle + the usage meters both removed from here
  // (Rahim, 2026-09-01) — heading was redundant with AiSettings.jsx's
  // PanelHeading above, and the usage meters moved up into the same row as
  // the Create New Agent/Knowledge Sources buttons (in AiSettings.jsx) so
  // they sit truly in-line instead of on their own row below.
  // See [[ai-settings-page-rebuild-plan]].
  //
  // Get Started panel — was a side-column layout sharing row space with the
  // cards (several iterations, see [[ai-agents-card-session-2026-09-02]]);
  // Rahim asked for my opinion on top-of-page vs. a bottom-right floating
  // chat-widget-style launcher (2026-09-02) and went with the floating one —
  // it's a mock chat-widget preview, so it sits where a real one would; it
  // also means the cards never have to share width with it at all, so this
  // grid is back to plain full-width auto-fill, no column-splitting logic.
  return (
    <Box>
      {/* Empty state */}
      {!loading && agents.length === 0 && (
        <Box
          sx={{
            textAlign: "center", py: 8,
            border: "1px dashed", borderColor: "divider", borderRadius: 2,
          }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary">No agents yet</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            Create one to use it in a workflow's "AI Reply" action.
          </Typography>
        </Box>
      )}

      {/* Cards grid — fixed columns, auto-filling the full page width now
          that there's no side panel to share it with. 412->340 (Rahim,
          2026-09-03: "make these card small as well ... for the laptop
          view meaning basic responsiveness") — 412px only fit 2 per row on
          a real laptop-width window with dead space after; 340 fits 3.
          340->380 (Rahim, 2026-09-04: "make it bigger") to match the
          enlarged card content above.
          `min(380px,100%)` -> `minmax(380px,1fr)` (Rahim, same day,
          screenshot: "too much blank space to the right side ... not make
          the cards too big") — fixed-width tracks left the row's leftover
          width empty after the last column; minmax+1fr keeps 380px as the
          floor (same column count as before) but lets those columns stretch
          to fill the row evenly. Card padding/gaps are flex-based and text
          is fixed rem sizes, so wider cards don't restretch the font scale
          — only the card's own whitespace grows. */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 2 }}>
        {loading && [0, 1, 2].map((i) => <SkeletonCard key={i} />)}
        {!loading && agents.map((agent) => (
          <AgentCard
            key={agent._id}
            agent={agent}
            workflowCounts={workflowCounts}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleActive={onToggleActive}
          />
        ))}
        {!loading && <CreateAgentTile onCreateNew={onCreateNew} />}
      </Box>

      {/* Temporarily disabled (Rahim, 2026-09-05) — commented out for now, will re-enable later */}
      {/* <AgentsGetStartedPanel hasRealAgents={agents.length > 0} /> */}
    </Box>
  );
}
